import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { LoanTerms } from '../../common/types';
import { monthlyMortgagePaymentsWithExtraPayments } from '../../mortrage-lib/extra-monthly-payments';
import {
  ABComparisonContextType,
  ABComparisonDataActions,
  ABComparisonDataActionsTypes,
  ABComparisonState,
  MonthlyPayment
} from '../types';
import {
  setDefaults,
  setExtraPayment,
  setInterestRate,
  setLoanAmount,
  setLoanTerm,
  setShowFull
} from './ABComparisonSetters';

const initialState: ABComparisonState = {
  A: {
    loanAmount: 250000,
    loanTerm: LoanTerms.THIRTY,
    interestRate: 6.725,
    extraPayment: 0
  },
  B: {
    loanAmount: 250000,
    loanTerm: LoanTerms.FIFTEEN,
    interestRate: 6.725,
    extraPayment: 0
  },
  showFull: false
};

export const LoanTermToNumber = {
  [LoanTerms.THIRTY]: 30,
  [LoanTerms.TWENTY]: 20,
  [LoanTerms.FIFTEEN]: 15
};

// eslint-disable-next-line abcsize/abcsize
function calculatorReducer(
  state: ABComparisonState,
  action: ABComparisonDataActions
): ABComparisonState {
  switch (action.type) {
    case ABComparisonDataActionsTypes.SET_LOAN_AMOUNT:
      return setLoanAmount(state, action.payload);
    case ABComparisonDataActionsTypes.SET_LOAN_TERM:
      return setLoanTerm(state, action.payload);
    case ABComparisonDataActionsTypes.SET_INTEREST_RATE:
      return setInterestRate(state, action.payload);
    case ABComparisonDataActionsTypes.SET_EXTRA_PAYMENT:
      return setExtraPayment(state, action.payload);
    case ABComparisonDataActionsTypes.SET_SHOW_FULL:
      return setShowFull(state, action.payload);
    case ABComparisonDataActionsTypes.SET_DEFAULTS:
      return setDefaults(state, initialState);
    default:
      throw new Error(`Unhandled action type`);
  }
}

const ABComparisonContext = createContext<ABComparisonContextType>({
  state: initialState,
  dispatch: () => null,
  getMonthlyPayment: () => ({ A: 0, B: 0 }),
  getTotalCost: () => ({ A: 0, B: 0 }),
  getTotalTerm: () => ({ A: 0, B: 0 }),
  getPayments: () => null
});

export function ABComparisonProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const resultVariantA = monthlyMortgagePaymentsWithExtraPayments({
    loanAmount: state.A.loanAmount,
    interestRate: state.A.interestRate,
    termInYears: LoanTermToNumber[state.A.loanTerm],
    extraPaymentAmount: state.A.extraPayment
  });
  const resultVariantB = monthlyMortgagePaymentsWithExtraPayments({
    loanAmount: state.B.loanAmount,
    interestRate: state.B.interestRate,
    termInYears: LoanTermToNumber[state.B.loanTerm],
    extraPaymentAmount: state.B.extraPayment
  });

  let getMonthlyPayment = () => ({ A: 0, B: 0 });
  let getTotalCost = () => ({ A: 0, B: 0 });
  let getTotalTerm = () => ({ A: 0, B: 0 });
  let getPayments = (): { A: MonthlyPayment[]; B: MonthlyPayment[] } | null => null;

  if (!('error' in resultVariantA) && !('error' in resultVariantB)) {
    getMonthlyPayment = () => ({
      A: resultVariantA.withExtraPayment.totalMonthlyPayment,
      B: resultVariantB.withExtraPayment.totalMonthlyPayment
    });
    getTotalCost = () => ({
      A: resultVariantA.withExtraPayment.totalCost,
      B: resultVariantB.withExtraPayment.totalCost
    });
    getTotalTerm = () => ({
      A: resultVariantA.withExtraPayment.payments?.length,
      B: resultVariantB.withExtraPayment.payments?.length
    });
    getPayments = () => ({
      A: resultVariantA.withExtraPayment.payments,
      B: resultVariantB.withExtraPayment.payments
    });
  } else {
    console.log('calculation error');
  }

  const value = {
    state,
    dispatch,
    getMonthlyPayment,
    getTotalCost,
    getTotalTerm,
    getPayments
  };

  return <ABComparisonContext.Provider value={value}>{children}</ABComparisonContext.Provider>;
}

export function useABComparison() {
  const context = useContext(ABComparisonContext);
  if (context === undefined) {
    throw new Error('useABComparison must be used within a ABComparisonProvider');
  }
  return context;
}
